import React from "react";
import { Box, Container, LinearProgress, Paper, Typography } from "@mui/material";
import { CheckCircle as CheckIcon } from "@mui/icons-material";

const defaultPalette = {
  currentGradient: "linear-gradient(135deg, #0D1C4F 0%, #1E3A8A 100%)",
  activeColor: "#016f57",
  inactiveColor: "rgba(148,163,184,.3)",
  currentShadow: "0 4px 12px rgba(30,58,138,.35)",
  activeShadow: "0 2px 8px rgba(1,111,87,.3)",
  textColor: "#1e293b",
  inactiveTextColor: "#64748b",
  connectorActiveColor: "#e2e8f0",
  connectorInactiveColor: "rgba(148,163,184,.3)",
  dotGradient: "linear-gradient(135deg, #0D1C4F 0%, #1E3A8A 100%)",
  dotShadow: "0 2px 8px rgba(30,58,138,.35)",
  dotSize: 10,
  progressClassName: "progress-bar",
};

const StepIndicator = ({ index, label, currentStep, palette }) => {
  const stepNumber = index + 1;
  const active = stepNumber <= currentStep;
  const current = stepNumber === currentStep;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: { xs: 0.25, sm: 0.5 },
        minWidth: { xs: 48, sm: 72 },
        textAlign: "center",
      }}
    >
      <Box
        sx={{
          width: { xs: 22, sm: 30 },
          height: { xs: 22, sm: 30 },
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: current
            ? palette.currentGradient
            : active
            ? palette.activeColor
            : palette.inactiveColor,
          color: "white",
          fontWeight: 700,
          fontSize: { xs: 9, sm: 12 },
          transform: current ? "scale(1.06)" : "scale(1)",
          boxShadow: current
            ? palette.currentShadow
            : active
            ? palette.activeShadow
            : "none",
          transition: "all .3s ease",
        }}
      >
        {active && !current ? (
          <CheckIcon sx={{ fontSize: { xs: 11, sm: 16 } }} />
        ) : (
          stepNumber
        )}
      </Box>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          color: current || active ? palette.textColor : palette.inactiveTextColor,
          opacity: current ? 1 : active ? 0.95 : 0.8,
          textAlign: "center",
          fontSize: { xs: "0.6rem", sm: "0.8rem" },
          lineHeight: 1.2,
          whiteSpace: { xs: "normal", sm: "nowrap" },
          px: { xs: 0.5, sm: 0 },
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};

const RegistrationStepper = ({
  steps,
  currentStep = 1,
  totalSteps,
  palette = {},
  containerProps = {},
  paperProps = {},
  progressClassName,
}) => {
  const mergedPalette = { ...defaultPalette, ...palette };
  const stepCount = steps?.length ?? 0;
  const resolvedTotal = totalSteps ?? stepCount;
  const safeCurrentStep = Math.min(
    Math.max(currentStep, 1),
    resolvedTotal > 0 ? resolvedTotal : 1
  );
  const progress = resolvedTotal > 1
    ? ((safeCurrentStep - 1) / (resolvedTotal - 1)) * 100
    : 0;

  // show a small initial progress so the bar isn't completely empty on step 1
  const progressAdjusted = resolvedTotal > 1 && progress < 6 ? 6 : progress;

  const resolvedSteps = stepCount
    ? steps
    : Array.from({ length: resolvedTotal }, (_, idx) => `Step ${idx + 1}`);

  const { sx: containerSx, ...restContainerProps } = containerProps;
  const {
    sx: paperSx,
    className: paperClassName = "stepper-paper",
    ...restPaperProps
  } = paperProps;

  const progressClass = progressClassName ?? mergedPalette.progressClassName;

  return (
    <Container
      maxWidth="lg"
      sx={{ pt: { xs: 1.5, sm: 2 }, pb: { xs: 0.75, sm: 1 }, px: { xs: 1, sm: undefined }, ...containerSx }}
      {...restContainerProps}
    >
      <Paper
        className={paperClassName}
        sx={{ py: { xs: 0.75, sm: 1.25 }, ...paperSx }}
        {...restPaperProps}
      >
        <Box sx={{ position: 'relative' }}>
          <LinearProgress
            variant="determinate"
            value={progressAdjusted}
            className={progressClass}
            sx={{ height: 4, borderRadius: 2 }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: -2,
              right: -1,
              width: mergedPalette.dotSize,
              height: mergedPalette.dotSize,
              borderRadius: '50%',
              background: safeCurrentStep === resolvedTotal ? mergedPalette.dotGradient : mergedPalette.connectorActiveColor,
              border: safeCurrentStep === resolvedTotal ? 'none' : `1px solid ${mergedPalette.connectorInactiveColor}`,
              boxShadow: safeCurrentStep === resolvedTotal ? mergedPalette.dotShadow : '0 0 0 1px rgba(0,0,0,0.02)',
              zIndex: 2,
              pointerEvents: 'none',
            }}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            flexWrap: { xs: "wrap", sm: "nowrap" },
            justifyContent: "space-between",
            alignItems: "center",
            gap: { xs: 0.25, sm: 0.75 },
            px: { xs: 0.25, sm: 0.75 },
            rowGap: { xs: 0.5, sm: 0 },
          }}
        >
          {resolvedSteps.map((label, index) => {
            const isLast = index === resolvedSteps.length - 1;
            const connectorColor = index < safeCurrentStep - 1
              ? mergedPalette.connectorActiveColor
              : mergedPalette.connectorInactiveColor;

            return (
              <React.Fragment key={label}>
                <StepIndicator
                  index={index}
                  label={label}
                  currentStep={safeCurrentStep}
                  palette={mergedPalette}
                />
                {!isLast && (
                  <Box
                    sx={{
                      flex: 1,
                      height: 1.5,
                      minWidth: { xs: 16, sm: 28 },
                      bgcolor: connectorColor,
                      borderRadius: 1,
                      mx: { xs: 0.2, sm: 0.5 },
                      my: { xs: 0.2, sm: 0 },
                    }}
                  />
                )}
              </React.Fragment>
            );
          })}
        </Box>
      </Paper>
    </Container>
  );
};

export default RegistrationStepper;
